import { useCallback, useEffect, useState } from 'react';

import { CATEGORIES, type CategoryId, type Preferences } from '../types';

// Empty string → relative URL (deployed, same-domain). Full URL → local dev override only.
const API_BASE = process.env.PREFERENCE_API_BASE ?? '';

const ALL_OFF: Preferences = CATEGORIES.reduce(
  (acc, c) => ({ ...acc, [c.id]: false }),
  {} as Preferences,
);

const ALL_ON: Preferences = CATEGORIES.reduce(
  (acc, c) => ({ ...acc, [c.id]: true }),
  {} as Preferences,
);

export interface UsePreferences {
  preferences: Preferences;
  userEmail: string;
  isLoading: boolean;
  error: string | null;
  updatePreference: (id: CategoryId, value: boolean) => void;
  unsubscribeAll: () => void;
  restore: (id: CategoryId) => void;
}

export function usePreferences(): UsePreferences {
  const token = new URLSearchParams(window.location.search).get('token') ?? '';

  const [preferences, setPreferences] = useState<Preferences>(ALL_ON);
  const [userEmail, setUserEmail] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setError('Missing token');
      setIsLoading(false);
      return;
    }
    fetch(`${API_BASE}/api/preferences?token=${encodeURIComponent(token)}`)
      .then((r) => {
        if (r.status === 401) throw new Error('Invalid or expired token');
        if (!r.ok) throw new Error(`Unexpected error (HTTP ${r.status})`);
        return r.json();
      })
      .then((data: { email: string; preferences: Record<string, boolean> }) => {
        setUserEmail(data.email);
        setPreferences(data.preferences as Preferences);
      })
      .catch((err: Error) => setError(err.message))
      .finally(() => setIsLoading(false));
  }, [token]);

  const patch = useCallback(
    (delta: Partial<Preferences>) => {
      fetch(`${API_BASE}/api/preferences?token=${encodeURIComponent(token)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ preferences: delta }),
      }).catch((err: Error) => console.error('Failed to update preferences', err.message));
    },
    [token],
  );

  const updatePreference = useCallback(
    (id: CategoryId, value: boolean) => {
      setPreferences((prev) => ({ ...prev, [id]: value }));
      patch({ [id]: value });
    },
    [patch],
  );

  const unsubscribeAll = useCallback(() => {
    setPreferences(ALL_OFF);
    patch(ALL_OFF);
  }, [patch]);

  const restore = useCallback(
    (id: CategoryId) => {
      setPreferences((prev) => ({ ...prev, [id]: true }));
      patch({ [id]: true });
    },
    [patch],
  );

  return { preferences, userEmail, isLoading, error, updatePreference, unsubscribeAll, restore };
}
